import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ContactInfo } from "@/components/ContactInfo";
import { Link } from "react-router-dom";
import { Gift, Users, Instagram, Tag, Clock, ShieldCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const RewardsPolicy = () => {
  const [rewardsEnabled, setRewardsEnabled] = useState(false);
  const [rewardTiers, setRewardTiers] = useState<{ referrals_needed: number; reward_description: string }[]>([]);
  const [igHashtag, setIgHashtag] = useState("#Inparadisebeauty");
  const [igHandle, setIgHandle] = useState("@paridisebeautyinc_");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", ["reward_program_enabled", "reward_tiers", "ig_required_hashtag", "instagram_handle"]);
      let hashtag = "#Inparadisebeauty";
      let handle = "@paridisebeautyinc_";
      data?.forEach((s: any) => {
        if (s.key === "reward_program_enabled") setRewardsEnabled(s.value === "true");
        if (s.key === "reward_tiers") {
          try { setRewardTiers(JSON.parse(s.value)); } catch {}
        }
        if (s.key === "ig_required_hashtag") hashtag = s.value || "#Inparadisebeauty";
        if (s.key === "instagram_handle") handle = s.value || "@paridisebeautyinc_";
      });
      setIgHashtag(hashtag);
      setIgHandle(handle);
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-32 pb-24 text-center text-muted-foreground text-sm">Loading…</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        path="/rewards-policy"
        title="Rewards & Referral Policy — In Paradise Beauty"
        description="Learn how to earn credits and rewards through referrals and Instagram shares at In Paradise Beauty."
      />
      <Navbar />
      <section className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center mb-12">
            <p className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-3">Legal</p>
            <h1 className="font-serif text-4xl md:text-5xl font-bold italic">Rewards & Referral Policy</h1>
            <p className="text-muted-foreground text-sm mt-4 font-light max-w-lg mx-auto">
              Everything you need to know about earning, redeeming, and managing your rewards at In Paradise Beauty.
            </p>
          </div>

          <div className="prose prose-sm max-w-none text-muted-foreground font-light leading-[2] tracking-wide space-y-8">
            <p className="text-xs text-muted-foreground/70 uppercase tracking-[0.3em]">
              Last Updated: March 17, 2026
            </p>

            {/* Program Overview */}
            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3 flex items-center gap-2">
                <Gift size={18} className="text-primary" /> 1. Program Overview
              </h2>
              <p>
                The In Paradise Beauty Rewards Program allows registered clients to earn credits and rewards through referrals and promotional activities. The program is currently <strong className="text-primary">{rewardsEnabled ? "active" : "inactive"}</strong>.
              </p>
              <p>
                Participation is free and automatic for all registered account holders. We reserve the right to modify, suspend, or discontinue the program at any time with or without notice.
              </p>
            </div>

            {/* Referral Program */}
            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3 flex items-center gap-2">
                <Users size={18} className="text-primary" /> 2. Referral Program
              </h2>
              <p>
                Every registered client receives a unique referral code displayed on their account page. Share this code with friends and family to earn rewards.
              </p>
              <div className="mt-3 p-4 border border-primary/20 bg-primary/5 rounded-md">
                <p className="text-sm font-medium text-primary mb-1">💰 Automatic $5 Credit Per Referral</p>
                <p className="text-sm">
                  For every friend who signs up with your referral code and completes their first paid appointment, you automatically receive a <strong className="text-primary">$5 credit</strong> added to your account. There is no limit to the number of referrals you can make!
                </p>
              </div>
              <ul className="list-disc pl-6 space-y-1 mt-3">
                <li>New clients must enter the referral code during sign-up for the referral to count.</li>
                <li>The referred client must complete at least one paid booking for the referral to be validated.</li>
                <li>A $5 coupon is automatically issued to you upon successful referral completion.</li>
                <li>Self-referrals, duplicate accounts, or fraudulent activity will disqualify both parties from all rewards.</li>
                <li>There is no limit to the number of referrals you can make.</li>
              </ul>

              {rewardTiers.length > 0 && (
                <div className="mt-5">
                  <p className="text-sm font-medium text-foreground mb-3">Referral Reward Tiers</p>
                  <div className="border border-border/50 rounded-lg overflow-hidden">
                    <div className="grid grid-cols-2 bg-secondary/50 px-4 py-2.5 border-b border-border/30">
                      <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium">Milestone</span>
                      <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium text-right">Reward</span>
                    </div>
                    {/* Automatic $5 per referral - always first */}
                    <div className="grid grid-cols-2 px-4 py-3.5 text-sm border-b border-primary/20 bg-primary/5">
                      <span className="text-primary font-medium">Every successful referral</span>
                      <span className="text-primary font-bold text-right">$5 credit (automatic)</span>
                    </div>
                    {rewardTiers.map((tier, i) => (
                      <div key={i} className={`grid grid-cols-2 px-4 py-3.5 text-sm ${i > 0 ? "border-t border-border/20" : "border-t border-border/20"}`}>
                        <span className="text-muted-foreground">{tier.referrals_needed} successful referral{tier.referrals_needed !== 1 ? "s" : ""}</span>
                        <span className="text-primary font-medium text-right">{tier.reward_description}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground/60 mt-2 italic">
                    Reward tiers are subject to change. Current tiers are pulled live from the system.
                  </p>
                </div>
              )}
            </div>

            {/* Instagram Rewards */}
            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3 flex items-center gap-2">
                <Instagram size={18} className="text-primary" /> 3. Instagram Share Rewards
              </h2>
              <p>
                Earn a <strong className="text-primary">$5 credit</strong> by sharing your In Paradise Beauty experience on Instagram. Here's how it works:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-3">
                <li>Post a photo or video of your results on Instagram.</li>
                <li><strong className="text-foreground">You must tag <span className="text-primary">{igHandle}</span></strong> in your post.</li>
                <li><strong className="text-foreground">You must include the hashtag <span className="text-primary">{igHashtag}</span></strong> in your caption.</li>
                <li><strong className="text-foreground">Include your referral code</strong> in the caption so we can identify your account.</li>
                <li>Submit the Instagram post link through your account page.</li>
                <li>Posts will be checked for the required tag and hashtag. Posts missing these will be flagged and may not be eligible for credit.</li>
                <li>An admin will review and approve your post. Some accounts may have auto-approval enabled.</li>
                <li>Upon approval, a $5 coupon code is automatically generated and added to your account.</li>
                <li>Instagram reward coupons expire <strong>6 months</strong> from the date of approval.</li>
              </ul>
              <div className="mt-3 p-3 border border-amber-500/20 bg-amber-500/5 rounded-md">
                <p className="text-sm font-medium text-foreground mb-1">⚠️ Eligibility Requirements</p>
                <p className="text-sm">
                  To be eligible for Instagram credit, your post <strong>must</strong> tag <strong className="text-primary">{igHandle}</strong> and include <strong className="text-primary">{igHashtag}</strong>. Posts that do not meet these criteria will be marked as ineligible and will not receive credit.
                </p>
              </div>
            </div>

            {/* Coupon Rules */}
            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3 flex items-center gap-2">
                <Tag size={18} className="text-primary" /> 4. Coupon & Credit Rules
              </h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Credits are issued as coupon codes tied to your account.</li>
                <li>Coupons are applied automatically at checkout when booking a service.</li>
                <li>Multiple coupons may be stacked on a single booking.</li>
                <li>Coupons cannot be redeemed for cash, transferred to another person, or applied retroactively to past bookings.</li>
                <li>Each coupon can only be used once. Once applied, it is marked as used and cannot be reused.</li>
                <li>Coupons may be issued for various reasons including referrals, Instagram shares, loyalty rewards, promotions, or admin gifts.</li>
              </ul>
            </div>

            {/* Expiration */}
            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3 flex items-center gap-2">
                <Clock size={18} className="text-primary" /> 5. Expiration Policy
              </h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>All coupons have an expiration date displayed on your account page.</li>
                <li>Standard coupons expire <strong>90 days</strong> from the date of issue unless otherwise specified.</li>
                <li>Instagram share coupons expire <strong>6 months</strong> from the date of approval.</li>
                <li>Admin-issued coupons may have custom expiration dates.</li>
                <li>Expired coupons cannot be reinstated, extended, or honored under any circumstances.</li>
                <li>You may receive a reminder email before your coupon expires (if enabled by the admin).</li>
              </ul>
            </div>

            {/* Terms & Conditions */}
            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3 flex items-center gap-2">
                <ShieldCheck size={18} className="text-primary" /> 6. General Terms
              </h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>In Paradise Beauty reserves the right to void any rewards obtained through fraud, abuse, or violation of these terms.</li>
                <li>We may modify reward amounts, tiers, and program rules at any time. Changes will be reflected on this page automatically.</li>
                <li>Rewards have no cash value and are void where prohibited by law.</li>
                <li>This policy is governed by and subject to the <Link to="/terms-of-use" className="text-primary underline hover:text-primary/80 transition-colors">Terms of Use</Link>.</li>
              </ul>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">7. Contact</h2>
              <p>
                For questions about the rewards program or your rewards balance, please contact us at:
              </p>
              <ContactInfo />
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default RewardsPolicy;
